/**
 * Stat-card group geometry — resolves the row of summary cards that heads a
 * grid page or a dashboard into either that page's own multi-column grid or,
 * at phone width, a swipeable one-card-at-a-time carousel.
 *
 * Every stat-card row in the product shares this resolver, so the phone
 * treatment is decided once instead of per page: an N-column grid squeezed
 * into a phone gives cards too narrow to read, so the same cards become a
 * horizontally snapped scroller showing one usable card with the next one
 * peeking as the affordance to swipe.
 *
 * Contains:
 * - DEFAULT_CAROUSEL_BREAKPOINT / DEFAULT_GROUP_GAP — group defaults
 * - CAROUSEL_CARD_BASIS — one usable card width, sized to leave the peek
 * - CARDS_PER_ROW — the widest row each viewport step reads comfortably
 * - balanceRow / resolveWrappedColumns — card count → cards per row
 * - resolveGridTemplateColumns — per-breakpoint card counts → CSS tracks
 * - buildStatCardGroupSx — the sx for whichever mode is active
 *
 * Returns plain JS — no JSX.
 */

/** Below the app's shared phone cutoff a group is a carousel. */
export const DEFAULT_CAROUSEL_BREAKPOINT = 'mobile';

/** Matches the inter-card spacing the grid pages already use. */
export const DEFAULT_GROUP_GAP = 'var(--page-gap)';

/**
 * Width of one carousel card. The percentage leaves the next card partly
 * visible — that peek, not a control, is what tells a reader the row scrolls —
 * and the rem cap stops a single card from stretching absurdly wide on the
 * widest phones.
 */
export const CAROUSEL_CARD_BASIS = 'min(82%, 17rem)';

/** Keeps the cards' drop shadow inside the scroller's clipped box. */
const CAROUSEL_SHADOW_GUTTER = '4px';

/**
 * Widest row each viewport step reads comfortably. Four across is the figure
 * the desktop pages are designed around; only an ultrawide adds a column,
 * because stretching four cards over 2000px of viewport reads as a gap with
 * numbers in it rather than a row. Below the carousel cutoff these are unused.
 */
export const CARDS_PER_ROW = { xs: 2, sm: 3, md: 4, xl: 6 };

/**
 * Cards per row that fills the rows it needs as evenly as it can.
 *
 * A group that simply took the maximum per row would leave the last row
 * holding whatever was left over — nine cards four-up is 4/4/1, a lone card
 * beside three empty columns. Taking the fewest rows that fit and then
 * spreading the cards over them gives 3/3/3 instead, so the leftover is at
 * worst one or two cells and never a full row of emptiness.
 *
 * @param {number} count - how many cards the group holds.
 * @param {number} max - the widest row this viewport reads comfortably.
 * @returns {number} Cards per row, never more than `max` and never below 1.
 */
export function balanceRow(count, max) {
  const cap = Number.isFinite(max) ? Math.max(1, Math.floor(max)) : 1;
  if (!Number.isFinite(count) || count < 1) return 1;
  const rows = Math.ceil(count / cap);
  return Math.ceil(count / rows);
}

/**
 * @param {number} count - how many cards the group holds.
 * @param {Object<string, number>} [perRow] - widest row per MUI breakpoint key.
 * @returns {Object<string, number>} Cards per row at each of those keys, ready
 *   to hand to `StatCardGroup`'s `columns`.
 */
export function resolveWrappedColumns(count, perRow = CARDS_PER_ROW) {
  return Object.fromEntries(Object.entries(perRow).map(([breakpoint, max]) => [breakpoint, balanceRow(count, max)]));
}

/**
 * @param {number} count - a card count. Groups size themselves off a summary
 *   array that can arrive empty, and `repeat(0, 1fr)` drops the whole rule.
 * @returns {string} A `grid-template-columns` value of at least one column.
 */
function toColumnTrack(count) {
  const columns = Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1;
  return `repeat(${columns}, 1fr)`;
}

/**
 * @param {number|Object<string, number>} columns - card count per MUI breakpoint
 *   key (`{ xs: 2, md: 5 }`), or a single count that applies at every width.
 * @returns {string|Object<string, string>} A `grid-template-columns` value,
 *   responsive when `columns` was.
 */
export function resolveGridTemplateColumns(columns) {
  if (columns == null) return toColumnTrack(1);
  if (typeof columns === 'number') return toColumnTrack(columns);
  return Object.fromEntries(Object.entries(columns).map(([breakpoint, value]) => [breakpoint, toColumnTrack(value)]));
}

/**
 * @param {object} params
 * @param {boolean} params.isCarousel - true below the group's carousel breakpoint.
 * @param {number|Object<string, number>} [params.columns] - grid-mode card count
 *   per breakpoint; ignored in carousel mode.
 * @param {number|string} [params.gap] - inter-card spacing, in both modes.
 * @returns {object} An MUI `sx` object for the group container.
 */
export function buildStatCardGroupSx({ isCarousel, columns, gap = DEFAULT_GROUP_GAP }) {
  if (!isCarousel) {
    return {
      display: 'grid',
      gridTemplateColumns: resolveGridTemplateColumns(columns),
      gap,
    };
  }

  return {
    display: 'flex',
    flexDirection: 'row',
    // Cards in one row share the tallest card's height, so a card carrying a
    // helper line doesn't sit taller than its neighbours mid-swipe.
    alignItems: 'stretch',
    gap,
    // The scroller is the only horizontally scrollable box on the page: it
    // never grows past the column it sits in, so the cards can't push a
    // page-level horizontal scrollbar.
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: CAROUSEL_SHADOW_GUTTER,
    scrollSnapType: 'x mandatory',
    // A swipe that runs off the end stays in the carousel instead of chaining
    // into the page scroll or the browser's back gesture.
    overscrollBehaviorX: 'contain',
    WebkitOverflowScrolling: 'touch',
    // The peek is the affordance; a scrollbar under the row would only eat
    // vertical space on the shortest viewports.
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    '&:focus-visible': {
      outline: '2px solid var(--md-primary)',
      outlineOffset: '2px',
      borderRadius: 'var(--card-radius)',
    },
    // Doubled selector: StatCard's own root sets `flex: 1` for its grid life,
    // and a single-class parent selector ties with it on specificity.
    '&& > *': {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: CAROUSEL_CARD_BASIS,
      minWidth: 0,
      scrollSnapAlign: 'start',
      scrollSnapStop: 'always',
    },
  };
}
